import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Package, Mail, Lock, User, Phone, ArrowRight, Loader2, Truck, Send, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const RegisterPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const defaultRole = searchParams.get('role') === 'carrier' ? 'carrier' : 'sender';
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    phone: '',
    role: defaultRole,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    
    setLoading(true);
    
    try {
      await register({
        email: formData.email,
        password: formData.password,
        name: formData.name,
        phone: formData.phone,
        role: formData.role,
      });
      toast.success('Compte créé avec succès!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'inscription');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Left side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative bg-slate-900">
        <img
          src="https://images.unsplash.com/photo-1764792314927-a82d9996285f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHxjb3VyaWVyJTIwdmFuJTIwb24lMjBjaXR5JTIwc3RyZWV0fGVufDB8fHx8MTc3MDMxMDQ3N3ww&ixlib=rb-4.1.0&q=85"
          alt="Delivery van"
          className="absolute inset-0 w-full h-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-12">
          <div className="text-white">
            <h2 className="text-3xl font-['Barlow_Condensed'] font-bold uppercase mb-4">
              Rejoignez la communauté COLISFLASH
            </h2>
            <p className="text-slate-300">
              Des milliers de particuliers partagent déjà leurs trajets pour transporter des colis.
            </p>
          </div>
        </div>
      </div>
      
      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 overflow-y-auto">
        <div className="w-full max-w-md animate-fade-in">
          <Link to="/" className="inline-flex items-center gap-2 mb-8 text-slate-600 hover:text-slate-900 transition-colors">
            <Package className="w-6 h-6 text-orange-500" />
            <span className="font-['Barlow_Condensed'] text-xl font-bold text-slate-900">COLISFLASH</span>
          </Link>
          
          <h1 className="font-['Barlow_Condensed'] text-4xl font-bold text-slate-900 uppercase tracking-tight mb-2">
            Inscription
          </h1>
          <p className="text-slate-600 mb-6">
            Créez votre compte pour commencer à utiliser COLISFLASH.
          </p>
          
          {/* Role Selection */}
          <div className="grid grid-cols-2 gap-3 mb-6" data-testid="role-selection">
            <button
              type="button"
              onClick={() => setFormData({ ...formData, role: 'sender' })}
              className={`p-4 rounded-xl border-2 transition-all text-left ${
                formData.role === 'sender'
                  ? 'border-orange-500 bg-orange-50 shadow-sm'
                  : 'border-slate-200 bg-white hover:border-slate-300'
              }`}
              data-testid="role-sender-btn"
            >
              <Send className={`w-6 h-6 mb-2 ${formData.role === 'sender' ? 'text-orange-500' : 'text-slate-400'}`} />
              <p className={`font-semibold ${formData.role === 'sender' ? 'text-orange-700' : 'text-slate-700'}`}>Expediteur</p>
              <p className="text-xs text-slate-500 mt-1">Je veux envoyer des colis</p>
            </button>
            <button
              type="button"
              onClick={() => setFormData({ ...formData, role: 'carrier' })}
              className={`p-4 rounded-xl border-2 transition-all text-left ${
                formData.role === 'carrier'
                  ? 'border-orange-500 bg-orange-50 shadow-sm'
                  : 'border-slate-200 bg-white hover:border-slate-300'
              }`}
              data-testid="role-carrier-btn"
            >
              <Truck className={`w-6 h-6 mb-2 ${formData.role === 'carrier' ? 'text-orange-500' : 'text-slate-400'}`} />
              <p className={`font-semibold ${formData.role === 'carrier' ? 'text-orange-700' : 'text-slate-700'}`}>Cotransporteur</p>
              <p className="text-xs text-slate-500 mt-1">Je transporte des colis</p>
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="register-form">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-slate-700 font-medium">Nom complet</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="name"
                  type="text"
                  placeholder="Jean Dupont"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="pl-10 h-12 bg-white border-slate-200 focus:ring-orange-500"
                  required
                  data-testid="name-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700 font-medium">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="votre@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="pl-10 h-12 bg-white border-slate-200 focus:ring-orange-500"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-slate-700 font-medium">Téléphone</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="phone"
                  type="tel"
                  placeholder="06 12 34 56 78"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="pl-10 h-12 bg-white border-slate-200 focus:ring-orange-500"
                  required
                  data-testid="phone-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700 font-medium">Mot de passe</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="pl-10 pr-12 h-12 bg-white border-slate-200 focus:ring-orange-500"
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-slate-700 font-medium">Confirmer le mot de passe</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  className="pl-10 pr-12 h-12 bg-white border-slate-200 focus:ring-orange-500"
                  required
                  data-testid="confirm-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  data-testid="toggle-confirm-password-visibility"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white font-bold uppercase tracking-wide"
              data-testid="submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  Création...
                </>
              ) : (
                <>
                  Créer mon compte
                  <ArrowRight className="ml-2 w-5 h-5" />
                </>
              )}
            </Button>
          </form>
          
          <div className="mt-8 text-center">
            <p className="text-slate-600">
              Déjà un compte?{' '}
              <Link to="/login" className="text-orange-500 font-semibold hover:text-orange-600 transition-colors" data-testid="login-link">
                Se connecter
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
